import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { OrderedMap, Map, Set } from 'immutable';
import withErrors from 'containers/form-validation/error-wrapper';
import { FormattedMessage } from 'react-intl';
import Quote from 'models/quote';
import get from 'lodash/get';
import cx from 'classnames';

import EditableDropdown from 'components/common/input/editable-dropdown';

const propTypes = {
  addPrepurchaseLicensePlate: PropTypes.func.isRequired,
  className: PropTypes.string,
  dismissError: PropTypes.func.isRequired,
  errors: PropTypes.instanceOf(Map).isRequired,
  modifyRequiredFieldList: PropTypes.func.isRequired,
  selectedQuote: PropTypes.instanceOf(Quote).isRequired,
  setErrors: PropTypes.func.isRequired,
  vehicles: PropTypes.instanceOf(OrderedMap),
};

const defaultProps = {
  vehicles: OrderedMap(),
  className: '',
  requiredFieldErrors: Map(),
  requiredFields: Set(),
  shouldScrollToError: false,
};

export const REQUIRED_LICENSE_PLATE = 'requiredLicensePlate';

export class RequiredLicensePlate extends Component {
  static getDerivedStateFromProps(nextProps, prevState) {
    if (!nextProps.vehicles.equals(prevState.vehicles)) {
      const savedLicensePlates = nextProps.vehicles.map(v => `${v.plateNumber}`).toArray();
      return {
        ...prevState,
        savedLicensePlates: [
          ...prevState.savedLicensePlates,
          ...savedLicensePlates,
        ],
      };
    }

    return {
      ...prevState,
      couponCode: nextProps.couponCode,
      couponCodeExpanded: prevState.couponCodeExpanded || (!prevState.couponCode && nextProps.couponCode),
    };
  }
  constructor(props) {
    super(props);

    const savedLicensePlates = props.vehicles.map(v => `${v.plateNumber}`).toArray();

    this.state = {
      selectedLicensePlate: '',
      savedLicensePlates,
      inputValue: '',
      vehicles: props.vehicles,
    };

    this.onChange = this.onChange.bind(this);
    this.onInputChange = this.onInputChange.bind(this);
    this.onCreateOption = this.onCreateOption.bind(this);
    this.onSave = this.onSave.bind(this);
    this.onMenuOpen = this.onMenuOpen.bind(this);
  }

  componentWillUnmount() {
    // clears the error if user leaves while in error state
    this.dismissError();
  }

  dismissError() {
    this.props.dismissError({ error: REQUIRED_LICENSE_PLATE });
    this.props.modifyRequiredFieldList({ remove: REQUIRED_LICENSE_PLATE });
  }

  onCreateOption(inputValue) {
    const { setErrors } = this.props;
    if (inputValue.length > 12) {
      this.setState({ selectedLicensePlate: '' }, () => this.onSave());
      setErrors({
        [REQUIRED_LICENSE_PLATE]: {
          isValid: false,
          message: "Sorry, license plate can't exceed 12 characters.",
        },
      });
    } else {
      const { savedLicensePlates: options } = this.state;
      const newOptions = [inputValue, ...options];
      this.setState({
        savedLicensePlates: newOptions,
        selectedLicensePlate: inputValue,
      }, () => this.onSave());
    }
  }

  onChange(plate, action) {
    const { action: act } = action;
    const licensePlate = (act === 'clear' || act === 'pop-value') ? '' : (plate.value || plate);
    this.setState({ selectedLicensePlate: licensePlate }, () => this.onSave());
  }

  onInputChange(inputValue) {
    this.setState({ inputValue });
  }

  onMenuOpen() {
    this.dismissError();
  }

  onSave() {
    this.props.addPrepurchaseLicensePlate({ plateNumber: this.state.selectedLicensePlate });
  }

  render() {
    if (!get(this.props.selectedQuote.restrictions, 'capture_plate', false)) return null;

    const {
      selectedLicensePlate,
      savedLicensePlates,
      inputValue,
    } = this.state;
    const {
      errors,
    } = this.props;
    const isValid = errors.getIn([REQUIRED_LICENSE_PLATE, 'isValid'], true);
    const errorMessage = errors.getIn([REQUIRED_LICENSE_PLATE, 'message'], '');

    const wrapperClasses = cx({
      'margin-right-30 padding-20 background-color-blue-light-grey margin-top-20 margin-bottom-20': true,
      'border-style-solid border-side-all border-color-red ': this.props.className.match(/error/) && selectedLicensePlate === '',
    });

    return (
      <div className={wrapperClasses}>
        <div className="row margin-bottom-10">
          <div className="col-xs-12">
            <div className="text-color-dark-slate text-size-20 text-weight-bold">
              <span className="iconified-font iconified-warning-triangle margin-right-5 text-color-orange" />
              <FormattedMessage
                id="checkout.license-plate-required"
                defaultMessage="LICENSE PLATE REQUIRED"
              />
            </div>
          </div>
        </div>
        <div className="margin-top-10 margin-bottom-10 text-size-14 text-color-dark-slate">
          <FormattedMessage
            id="checkout.add-new-plate-or-select"
            defaultMessage="Add new plate or select from list."
          />
        </div>
        <div className="form-field-container margin-top-10 margin-bottom-10">
          <EditableDropdown
            isValid={isValid}
            placeholder="License Plate"
            formatCreateLabelText="Add Plate"
            onChange={this.onChange}
            selectedValue={selectedLicensePlate}
            savedValues={savedLicensePlates}
            inputValue={inputValue}
            onInputChange={this.onInputChange}
            onCreateOption={this.onCreateOption}
            onMenuOpen={this.onMenuOpen}
            noOptionsMessage="Enter or select plate"
            isClearable={false}
            classNamePrefix={REQUIRED_LICENSE_PLATE}
          />
          {!isValid ?
            <div className="error-popup">
              <FormattedMessage
                id="checkout.invalid-license-plate"
                defaultMessage={errorMessage}
              />
            </div> : null}
        </div>
        <div className="margin-top-10 text-size-14 text-color-dark-slate">
          <FormattedMessage
            id="checkout.license-plate-required-for-entry"
            defaultMessage="The facility needs this info to give you entry access."
          />
        </div>
      </div>
    );
  }
}

RequiredLicensePlate.propTypes = propTypes;
RequiredLicensePlate.defaultProps = defaultProps;
export default withErrors(RequiredLicensePlate);
